/**
 * Tâche 1287 (Eric 2026-06-30) — Barre de recherche d'actions (Ctrl+K).
 *
 * Trouve les ÉCRANS et les ACTIONS nommées (« copie fidèle », « détecter les
 * écarts », « scoring AMF », « importer des étiquettes »…). Sélectionner une
 * action navigue vers le bon écran ET surligne le bouton qui l'exécute
 * (cf. `useActionHighlight`) : on montre où cliquer, on ne clique pas à la
 * place de l'utilisateur.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, Lightning, Monitor } from "@phosphor-icons/react";

import { searchCommands } from "../lib/commandActions";

export default function CommandPalette({ lang = "fr" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const { actions, screens } = useMemo(() => searchCommands(query, lang), [query, lang]);
  const flat = useMemo(
    () => [
      ...actions.map((a) => ({ ...a, group: "action" })),
      ...screens.map((s) => ({ ...s, group: "screen" })),
    ],
    [actions, screens],
  );

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        setQuery("");
        setCursor(0);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const run = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    const url = item.focus
      ? `${item.path}${item.path.includes("?") ? "&" : "?"}act=${encodeURIComponent(item.focus)}`
      : item.path;
    navigate(url);
  }, [navigate]);

  if (!open) return null;

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(flat[cursor]);
    }
  };

  const renderRow = (item, idx) => (
    <li key={item.id}>
      <button
        type="button"
        onMouseEnter={() => setCursor(idx)}
        onClick={() => run(item)}
        className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-3 transition-colors ${
          idx === cursor ? "bg-[#002FA7] text-white" : "hover:bg-zinc-100 text-zinc-800"
        }`}
        data-testid={`cmdk-item-${item.id}`}
      >
        {item.group === "action"
          ? <Lightning size={15} weight="fill" className="shrink-0" />
          : <Monitor size={15} className="shrink-0" />}
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold truncate">{item.label}</span>
          {item.hint && (
            <span className={`block text-[11px] truncate ${idx === cursor ? "text-white/75" : "text-zinc-500"}`}>
              {item.hint}
            </span>
          )}
        </span>
        {item.focus && (
          <span className={`text-[10px] font-bold uppercase tracking-wide shrink-0 ${idx === cursor ? "text-white/80" : "text-[#002FA7]"}`}>
            {lang === "fr" ? "Action" : "Action"}
          </span>
        )}
      </button>
    </li>
  );

  let index = -1;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center bg-black/50 p-4 pt-[12vh]"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      data-testid="command-palette"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl overflow-hidden">
        <div className="flex items-center gap-2 px-3 border-b border-zinc-200">
          <MagnifyingGlass size={16} className="text-zinc-400 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setCursor(0); }}
            onKeyDown={onInputKey}
            placeholder={lang === "fr"
              ? "Chercher un écran ou une action (copie fidèle, détecter les écarts, scoring AMF…)"
              : "Search a screen or an action…"}
            className="flex-1 py-3 text-sm outline-none placeholder:text-zinc-400"
            data-testid="command-palette-input"
          />
          <kbd className="text-[10px] font-mono text-zinc-400 border border-zinc-200 rounded px-1.5 py-0.5 shrink-0">esc</kbd>
        </div>

        <div className="max-h-[55vh] overflow-y-auto p-2">
          {flat.length === 0 && (
            <p className="px-3 py-6 text-center text-xs text-zinc-500" data-testid="command-palette-empty">
              {lang === "fr" ? "Aucune action ni écran ne correspond." : "No matching action or screen."}
            </p>
          )}

          {actions.length > 0 && (
            <>
              <p className="px-3 pt-1 pb-1 text-[10px] font-bold uppercase tracking-wide text-zinc-400">
                {lang === "fr" ? "Actions" : "Actions"}
              </p>
              <ul data-testid="command-palette-actions">
                {actions.map((a) => { index += 1; return renderRow({ ...a, group: "action" }, index); })}
              </ul>
            </>
          )}

          {screens.length > 0 && (
            <>
              <p className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wide text-zinc-400">
                {lang === "fr" ? "Écrans" : "Screens"}
              </p>
              <ul data-testid="command-palette-screens">
                {screens.map((s) => { index += 1; return renderRow({ ...s, group: "screen" }, index); })}
              </ul>
            </>
          )}
        </div>

        <div className="px-3 py-2 border-t border-zinc-200 text-[10px] text-zinc-500 flex items-center gap-3">
          <span>↑ ↓ {lang === "fr" ? "naviguer" : "navigate"}</span>
          <span>↵ {lang === "fr" ? "ouvrir" : "open"}</span>
          <span>{lang === "fr" ? "Le bouton visé est surligné à l'arrivée" : "Target button gets highlighted"}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
